/**
 * 📋 SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ==============================================================================
 * FILE: frontend/src/pages/landing/components/LandingHeader.tsx
 * PURPOSE: Sticky landing header with nav links, language toggle, and CTAs
 * SDLC FRAMEWORK VERSION: 6.1.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * PILLAR: P1 (Stage 06 - BUILD) + P6 (UI/UX Design)
 * SPRINT GOVERNANCE: Rule 4: Tier-3 Code Review
 *
 * DATE: 2025-12-28
 * COMPLIANCE: SDLC 6.3.1 + Vietnamese UX Standards
 *
 * @business-context
 * - Domain: Marketing navigation, conversion to login
 * - Impact: Guides users to sections and CTA actions
 * - Stakeholders: Prospects, Sales, Product
 *
 * @technical-architecture
 * - Layer: Presentation Layer
 * - Pattern: Sticky header, CSS Modules
 * - Dependencies: react-i18next, react-router
 */

import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styles from "./LandingHeader.module.css";

export const LandingHeader: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const setLang = async (lng: "vi" | "en") => {
    await i18n.changeLanguage(lng);
    localStorage.setItem("nqh-language", lng);
    document.documentElement.lang = lng;
  };

  return (
    <header className={styles.header}>
      <div className={styles.inner}>
        <button
          type="button"
          className={styles.brand}
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          aria-label="NQH-Bot"
        >
          <span className={styles.logo}>☕</span>
          <span className={styles.brandText}>NQH-Bot</span>
        </button>

        <nav className={styles.nav} aria-label="Primary">
          <a className={styles.navLink} href="#features">
            {t("landing.header.features")}
          </a>
          <a className={styles.navLink} href="#use-cases">
            {t("landing.header.useCases")}
          </a>
          <a className={styles.navLink} href="#contact">
            {t("landing.header.contact")}
          </a>
        </nav>

        <div className={styles.actions}>
          <div className={styles.langToggle} aria-label="Language">
            <button
              type="button"
              className={`${styles.langButton} ${i18n.language.startsWith("vi") ? styles.langActive : ""}`}
              onClick={() => setLang("vi")}
            >
              VI
            </button>
            <button
              type="button"
              className={`${styles.langButton} ${i18n.language.startsWith("en") ? styles.langActive : ""}`}
              onClick={() => setLang("en")}
            >
              EN
            </button>
          </div>

          <button
            type="button"
            className={styles.ctaSecondary}
            onClick={() => navigate("/login")}
          >
            {t("landing.header.login")}
          </button>
          <button
            type="button"
            className={styles.ctaPrimary}
            onClick={() => navigate("/login")}
          >
            {t("landing.header.tryFree")}
          </button>
        </div>
      </div>
    </header>
  );
};
